import React from 'react';
import FavoriteButton from './FavoriteButton';
import ShareButtons from './ShareButtons';

interface ContractorDetailHeaderProps {
  contractorName: string;
  companyName: string;
  rating: number;
  reviews: number;
  profileImageUrl: string;
  isVerified: boolean;
  website?: string;
  location?: string;
  serviceAreas?: string[];
  isMobile?: boolean;
  isDesktop?: boolean;
  contractorId: number;
  initialIsFavorited: boolean;
  onToggleFavorite: (contractorId: number, isFavorited: boolean) => void;
  shareUrl?: string;
}

const ContractorDetailHeader: React.FC<ContractorDetailHeaderProps> = ({
  contractorName,
  companyName,
  rating,
  reviews,
  profileImageUrl,
  isVerified,
  website,
  location,
  serviceAreas,
  isMobile,
  isDesktop,
  contractorId,
  initialIsFavorited,
  onToggleFavorite,
  shareUrl,
}) => {
  const renderStars = (currentRating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (currentRating >= i) {
        stars.push(<span key={i} className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>);
      } else if (currentRating > i - 1) {
        stars.push(<span key={i} className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>star_half</span>);
      } else {
        stars.push(<span key={i} className="material-symbols-outlined text-lg">star</span>);
      }
    }
    return stars;
  };

  if (isMobile) {
    return (
      <section className="px-container-margin pt-stack-lg flex flex-col items-center md:hidden">
        <div className="relative">
          <img className="w-24 h-24 rounded-lg object-cover shadow-md border-2 border-white" alt={contractorName} src={profileImageUrl} />
          {isVerified && (
            <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-sm">
              <span className="material-symbols-outlined text-[#2E7D32]" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
            </div>
          )}
        </div>
        <div className="text-center mt-4 space-y-1">
          <h2 className="font-headline-md text-headline-md-mobile text-primary">{companyName}</h2>
          <div className="flex items-center justify-center gap-1">
            <span className="material-symbols-outlined text-[#FBC02D] text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
            <span className="font-label-bold text-label-bold text-on-surface">{rating.toFixed(1)}</span>
            <span className="font-body-md text-body-md text-outline">({reviews} Reviews)</span>
          </div>
          {location && (
            <div className="flex items-center justify-center gap-1 text-sm text-on-surface-variant">
              <span className="material-symbols-outlined text-[16px]">location_on</span>
              {location}
            </div>
          )}
          {website && (
            <a href={website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 text-sm text-primary hover:underline">
              <span className="material-symbols-outlined text-[16px]">language</span>
              {website.replace(/^https?:\/\//, '')}
            </a>
          )}
          {isVerified && (
            <span className="inline-block bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Verified Pro</span>
          )}
        </div>
      </section>
    );
  }

  if (isDesktop) {
    return (
      <aside className="md:col-span-4 space-y-gutter hidden md:block">
        <div className="bg-surface-container-lowest rounded-xl shadow-sm overflow-hidden border border-outline-variant">
          <div className="relative h-48 bg-primary-container">
            <img alt="Contractor Profile" className="w-full h-full object-cover" src={profileImageUrl} />
            {isVerified && (
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1">
                <span className="material-symbols-outlined text-emerald-600 text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
                <span className="font-label-bold text-label-bold text-on-surface">Verified Pro</span>
              </div>
            )}
          </div>
          <div className="p-stack-lg space-y-stack-md">
            <div className="space-y-stack-sm">
              <h2 className="font-headline-sm text-headline-sm text-primary">{contractorName}</h2>
              <div className="flex items-center gap-2">
                <div className="flex text-amber-400">
                  {renderStars(rating)}
                </div>
                <span className="font-label-bold text-label-bold text-on-surface">{rating.toFixed(1)}</span>
                <span className="font-label-md text-label-md text-outline">({reviews} reviews)</span>
              </div>
            </div>

            {/* Contact details */}
            <div className="pt-4 border-t border-outline-variant space-y-2">
              {location && (
                <div className="flex items-center gap-2 text-sm text-on-surface-variant">
                  <span className="material-symbols-outlined text-[18px]">location_on</span>
                  {location}
                </div>
              )}
              {website && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="material-symbols-outlined text-[18px] text-on-surface-variant">language</span>
                  <a href={website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                    {website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>

            {shareUrl && (
              <div className="pt-2 border-t border-outline-variant">
                <p className="text-xs text-on-surface-variant mb-2">Share this contractor</p>
                <ShareButtons url={shareUrl} title={contractorName} />
              </div>
            )}
          </div>
        </div>

        {/* Service areas */}
        {serviceAreas && serviceAreas.length > 0 && (
          <div className="bg-surface-container-lowest rounded-xl shadow-sm p-stack-lg border border-outline-variant space-y-stack-md">
            <h3 className="font-label-bold text-label-bold text-primary uppercase tracking-wider">Service Areas</h3>
            <div className="flex flex-wrap gap-2">
              {serviceAreas.map(area => (
                <span key={area} className="flex items-center gap-1 text-sm text-on-surface-variant bg-surface-container px-3 py-1 rounded-full border border-outline-variant/30">
                  <span className="material-symbols-outlined text-[14px]">location_on</span>
                  {area}
                </span>
              ))}
            </div>
          </div>
        )}
      </aside>
    );
  }

  return null;
};

export default ContractorDetailHeader;
